import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { TitleEditor } from "src/base-components/TitleEditor";
import { ClauseBlock, ClauseForm } from "src/clauseBlock/ClauseBlock";
import { BranchEffect } from "src/ruleNodeV2Editor/BranchEffect";
import { RuleNodeV2Form } from "src/ruleNodeV2Editor/types";
import { useDefaultEffectsValue } from "src/ruleNodeV2Editor/useDefaultEffectsValue";
import {
  mergeFieldIdAndName,
  RunErrorFieldNames,
} from "src/utils/FieldErrorUtils";

type Props = {
  index: number;
  id: string;
  immutable: boolean;
  lazyInputs: boolean;
  fieldErrors: FieldErrors | undefined;
  onRemove: () => void;
  hideActionButtons?: boolean;
};

export const Branch: React.FC<Props> = ({
  hideActionButtons,
  index: branchIndex,
  id,
  immutable,
  lazyInputs,
  fieldErrors,
  onRemove,
}) => {
  const { setValue, control } = useFormContext<RuleNodeV2Form>();
  const { remove } = useFieldArray({
    name: `branches.${branchIndex}.effects`,
  });

  const { listeners, setNodeRef, transform, transition } = useSortable({
    id: id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const defaultEffectsValue = useDefaultEffectsValue({
    onUnregister: (index) => remove(index),
    onSetValue: (index, value) =>
      setValue(`branches.${branchIndex}.effects.${index}.target`, value),
  });
  const branchEffectsValues = useWatch({
    control,
    name: `branches.${branchIndex}.effects`,
  });

  const handleChange = (value: ClauseForm) => {
    setValue(`branches.${branchIndex}.clauses`, value.clauses);
    setValue(`branches.${branchIndex}.junction`, value.junction);
  };

  return (
    <Card
      ref={setNodeRef}
      className="w-full space-y-3"
      data-loc={`branch-${branchIndex}`}
      style={style}
    >
      <Card.Header>
        <div className="flex flex-row items-center gap-x-1">
          <ReorderHandle
            className="-ml-2"
            immutable={immutable}
            listeners={listeners}
          />
          <Controller
            control={control}
            name={`branches.${branchIndex}.name`}
            render={({ field }) => (
              <TitleEditor
                className="w-full font-inter-medium-12px"
                dataLoc="rule-name-editor"
                disabled={immutable}
                placeholder={`Rule ${branchIndex + 1}`}
                value={field.value}
                onSubmit={field.onChange}
              />
            )}
          />
        </div>
        {!hideActionButtons && (
          <Button
            disabled={immutable}
            iconLeft={faTrashAlt}
            size="sm"
            variant="secondary"
            onClick={onRemove}
          >
            Delete rule
          </Button>
        )}
      </Card.Header>
      <Card.Content>
        <Controller
          control={control}
          name={`branches.${branchIndex}`}
          render={({ field }) => (
            <ClauseBlock
              clauses={field.value.clauses}
              fieldErrors={fieldErrors}
              hideActionButtons={hideActionButtons}
              immutable={immutable}
              junction={field.value.junction}
              lazyInputs={lazyInputs}
              onChange={handleChange}
            />
          )}
        />
        <div className="h-px rounded-full bg-gray-200" />
        {defaultEffectsValue.map((field, index) => (
          <BranchEffect
            key={field.id}
            branchIndex={branchIndex}
            fieldError={
              fieldErrors?.[
                mergeFieldIdAndName(
                  branchEffectsValues.at(index)?.id,
                  RunErrorFieldNames.RIGHT,
                )
              ]
            }
            immutable={immutable}
            index={index}
            lazyInputs={lazyInputs}
            name={field.target}
          />
        ))}
      </Card.Content>
    </Card>
  );
};
